import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import {
    listVaultsBySlugSearchCore,
    getGroupBySlugExactCore,
    createVaultCore,
    ensureOwnerMembershipCore,
    listUserVaultsWithRoleCore,
    updateGroupCore,
    uploadVaultLogoCore,
} from '@gv/core';
import { Eye, EyeOff, Upload, Search, Plus, ChevronLeft, Copy as CopyIcon, Check } from 'lucide-react';
import { useGroupContext } from '../lib/groups/context';

type OnboardingStep = 'signup' | 'choose-action' | 'find-vault' | 'create-vault';

interface VaultOnboardingProps {
    onComplete: () => void;
    initialStep?: OnboardingStep;
}

export default function VaultOnboarding({ onComplete, initialStep }: VaultOnboardingProps) {
    const [step, setStep] = useState<OnboardingStep>(() => initialStep || 'signup');
    const [email, setEmail] = useState('');
    // OTP-only signup
    const [otpSent, setOtpSent] = useState(false);
    const [otpCode, setOtpCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isAuthed, setIsAuthed] = useState(false);
    const [oauthLoading, setOauthLoading] = useState(false);

    // If user is already authenticated, always skip to create-vault
    React.useEffect(() => {
        (async () => {
            try {
                const { data } = await supabase.auth.getUser();
                const authed = !!data?.user;
                setIsAuthed(authed);
                // Only auto-advance to create-vault if user didn't explicitly request find-vault
                if (authed) {
                    const wantsFind = initialStep === 'find-vault';
                    if (!wantsFind && step !== 'create-vault') {
                        setStep('create-vault');
                    }
                }
            } catch { /* ignore */ }
        })();
        // Respect initialStep changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialStep]);

    // Find vault state
    const [searchSlug, setSearchSlug] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);

    // Create vault state
    const [vaultName, setVaultName] = useState('');
    const [vaultSlug, setVaultSlug] = useState('');
    const [vaultLogo, setVaultLogo] = useState<File | null>(null);
    const [vaultLogoPreview, setVaultLogoPreview] = useState('');
    const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
    const [checkingSlug, setCheckingSlug] = useState(false);
    // Visual flash for availability result
    const [slugFlash, setSlugFlash] = useState<null | 'green' | 'red'>(null);
    const debounceRef = useRef<number | null>(null);
    const { reload } = useGroupContext();
    // Base domain for public vault URLs. Override via VITE_PUBLIC_BASE_DOMAIN; fallback to production domain.
    const BASE_DOMAIN = (import.meta as any)?.env?.VITE_PUBLIC_BASE_DOMAIN || 'vaultcontact.com';
    const publicVaultUrl = (slug: string) => `https://${BASE_DOMAIN}/${slug || ''}`;
    const displayVaultUrl = (slug: string) => `${BASE_DOMAIN}/${slug || ''}`; // no protocol in preview
    const [copied, setCopied] = useState(false);

    const handleSignup = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            if (!otpSent) {
                const { error } = await supabase.auth.signInWithOtp({
                    email,
                    options: { shouldCreateUser: true },
                });
                if (error) throw error;
                setSuccess('Secure code sent to your email.');
                setOtpSent(true);
            } else {
                const { error } = await supabase.auth.verifyOtp({ email, token: otpCode, type: 'email' });
                if (error) throw error;
                setSuccess('Email verified!');
                setStep('choose-action');
            }
        } catch (error: any) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setOauthLoading(true);
        setError('');
        try {
            const redirectUrl = `${window.location.origin}/auth/google-callback.html`;
            const { data, error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: redirectUrl,
                    queryParams: { access_type: 'offline', prompt: 'consent' },
                },
            });
            if (error) throw error;
            if (data?.url) window.location.href = data.url;
        } catch (e: any) {
            console.error('Google OAuth start failed', e);
            setError(e?.message || 'Google sign-in failed to start');
        } finally {
            setOauthLoading(false);
        }
    };

    const searchVaults = async () => {
        if (!searchSlug.trim()) return;

        setSearchLoading(true);
        try {
            const rows = await listVaultsBySlugSearchCore(supabase, searchSlug);
            setSearchResults(rows);
        } catch (error) {
            console.error('Search error:', error);
            setSearchResults([]);
        } finally {
            setSearchLoading(false);
        }
    };

    const checkSlugAvailability = async (slug: string) => {
        if (!slug.trim()) {
            setSlugAvailable(null);
            return;
        }

        setCheckingSlug(true);
        try {
            const found = await getGroupBySlugExactCore(supabase, slug);
            const available = !found;
            setSlugAvailable(available);
            // Trigger a quick ring flash and persist color
            setSlugFlash(available ? 'green' : 'red');
            window.setTimeout(() => setSlugFlash(null), 700);
        } catch (error) {
            console.error('Slug check error:', error);
            setSlugAvailable(false);
            setSlugFlash('red');
            window.setTimeout(() => setSlugFlash(null), 700);
        } finally {
            setCheckingSlug(false);
        }
    };

    const handleVaultNameChange = (name: string) => {
        // Do NOT auto-generate the slug; user chooses it explicitly
        setVaultName(name);
    };

    const handleSlugChange = (slug: string) => {
        const cleanSlug = slug
            .toLowerCase()
            .replace(/[^a-z0-9-]/g, '')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');

        setVaultSlug(cleanSlug);
        // Debounce availability check; wait for the user to pause typing
        if (debounceRef.current) {
            window.clearTimeout(debounceRef.current);
        }
        setCheckingSlug(true);
        debounceRef.current = window.setTimeout(() => {
            checkSlugAvailability(cleanSlug);
        }, 1000);
    };

    // Clear any pending debounce on unmount
    useEffect(() => {
        return () => { if (debounceRef.current) window.clearTimeout(debounceRef.current); };
    }, []);

    const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setVaultLogo(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setVaultLogoPreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const uploadLogo = async (file: File): Promise<string> => {
        return uploadVaultLogoCore(supabase, file);
    };

    // TEMPORARY POLICY: Unlimited vaults allowed during development. Previous single-vault restriction removed.
    // Plan selection is client-side only; stored to group record for future pricing logic.
    type VaultPlan = 'personal' | 'family' | 'business';
    const [planChoice, setPlanChoice] = useState<VaultPlan>('personal');

    const createVault = async () => {
        if (!vaultName.trim() || !vaultSlug.trim() || !slugAvailable) {
            setError('Please provide a valid vault name and slug');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');

            // Upload logo if provided
            let logoUrl = '';
            if (vaultLogo) {
                logoUrl = await uploadLogo(vaultLogo);
            }

            // Create vault and attach chosen plan (if schema has plan field) – swallow errors if column absent.
            const vault = await createVaultCore(supabase, vaultSlug, vaultName);
            try {
                await updateGroupCore(supabase, vault.id, { image_url: logoUrl || null, plan: planChoice } as any);
            } catch { /* ignore missing column in dev */ }

            // Try to ensure owner membership with retry for constraint errors
            try {
                await ensureOwnerMembershipCore(supabase, vault.id, user.id);
            } catch (membershipError: any) {
                const errorMsg = membershipError?.message || '';

                if (errorMsg.includes('personal vaults may only have an owner')) {
                    // This is the constraint error - try to work around it by directly inserting
                    console.warn('Encountered personal vault constraint, attempting direct membership creation...');

                    try {
                        // Direct insert as a workaround
                        const { error: directError } = await supabase
                            .from('vault_members')
                            .insert({ group_id: vault.id, user_id: user.id, role: 'owner' });

                        if (directError) {
                            console.error('Direct membership creation also failed:', directError);
                            throw new Error(`Failed to create vault membership. This may be due to a database constraint that needs to be removed. Please check the database migrations.`);
                        }
                    } catch (directInsertError) {
                        console.error('Direct membership insert failed:', directInsertError);
                        throw new Error(`Vault created but unable to set ownership due to database constraints. Vault ID: ${vault.id}`);
                    }
                } else {
                    // Re-throw other errors
                    throw membershipError;
                }
            }

            if (logoUrl) {
                try { localStorage.setItem(`gv:logoUrl:${vaultSlug}`, logoUrl); } catch { }
            }
            try { localStorage.setItem(`gv:vaultName:${vaultSlug}`, vaultName); } catch { }
            try { localStorage.setItem(`gv:vaultPlan:${vault.id}`, planChoice); } catch { }

            try {
                window.history.pushState({}, '', `/${vaultSlug}`);
                reload();
            } catch {
                window.location.href = `/${vaultSlug}`;
            }
        } catch (error: any) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const joinVault = async (vaultSlug: string) => {
        // Navigate to the vault; VaultGate will handle access
        try {
            window.history.pushState({}, '', `/${vaultSlug}`);
            reload();
        } catch {
            window.location.href = `/${vaultSlug}`;
        }
    };

    const renderStep = () => {
        switch (step) {
            case 'signup':
                return (
                    <div className="space-y-6">
                        <div className="text-center">
                            <h2 className="text-3xl font-light text-white mb-3">Create Account</h2>
                            <p className="text-gray-400">Start your vault journey</p>
                        </div>

                        <form onSubmit={handleSignup} className="space-y-4">
                            {/* Removed 'Verify identity' label per request */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter your email"
                                />
                                <p className="mt-2 text-xs text-gray-400">One‑time encrypted access code.</p>
                            </div>

                            {otpSent && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Verification Code</label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        maxLength={6}
                                        value={otpCode}
                                        onChange={(e) => setOtpCode(e.target.value)}
                                        required
                                        className="w-full px-4 py-3 bg-gray-800 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter 6-digit code"
                                    />
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-3 bg-gray-900 text-white font-semibold rounded-lg border border-gray-700 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (otpSent ? 'Verifying access…' : 'Requesting secure access…') : (otpSent ? 'Verify Secure Code' : 'Request Secure Access')}
                            </button>
                            <div className="text-center pt-2">
                                <div className="text-center">
                                    <button
                                        type="button"
                                        onClick={handleGoogleSignIn}
                                        disabled={oauthLoading}
                                        className="text-gray-400 hover:text-gray-200 text-sm font-medium underline-offset-4 hover:underline disabled:opacity-50"
                                    >
                                        Continue with Google
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    onClick={onComplete}
                                    className="text-blue-400 hover:text-blue-300 underline text-sm"
                                >
                                    Back to sign in
                                </button>
                            </div>
                        </form>
                    </div>
                );

            case 'choose-action':
                return (
                    <div className="space-y-6">
                        <div className="text-center">
                            <h2 className="text-3xl font-light text-white mb-3">Welcome!</h2>
                            <p className="text-gray-400">What would you like to do?</p>
                        </div>

                        <div className="space-y-4">
                            <button
                                onClick={() => setStep('find-vault')}
                                className="w-full p-5 bg-gray-900 border border-gray-800 rounded-lg hover:bg-gray-800 transition-colors text-left"
                            >
                                <div className="flex items-center space-x-3">
                                    <Search className="w-6 h-6 text-gray-300" />
                                    <div>
                                        <h3 className="text-base font-medium text-white">Find Vault</h3>
                                        <p className="text-gray-400 text-sm">Search for an existing vault to join</p>
                                    </div>
                                </div>
                            </button>

                            <button
                                onClick={() => setStep('create-vault')}
                                className="w-full p-5 bg-gray-900 border border-gray-800 rounded-lg hover:bg-gray-800 transition-colors text-left"
                            >
                                <div className="flex items-center space-x-3">
                                    <Plus className="w-6 h-6 text-gray-300" />
                                    <div>
                                        <h3 className="text-base font-medium text-white">Create Vault</h3>
                                        <p className="text-gray-400 text-sm">Start your own vault</p>
                                    </div>
                                </div>
                            </button>
                        </div>

                        <div className="text-center pt-2">
                            <button
                                type="button"
                                onClick={onComplete}
                                className="text-blue-400 hover:text-blue-300 underline text-sm"
                            >
                                Back to sign in
                            </button>
                        </div>
                    </div>
                );

            case 'find-vault':
                return (
                    <div className="space-y-6">
                        <div className="text-center">
                            <h2 className="text-2xl font-light text-white mb-2">Find Vault</h2>
                            <p className="text-gray-400 text-sm">Search for a vault to join</p>
                        </div>

                        <div className="space-y-4">
                            <div className="relative">
                                <input
                                    type="text"
                                    value={searchSlug}
                                    onChange={(e) => setSearchSlug(e.target.value)}
                                    placeholder="Enter vault name or slug..."
                                    className="w-full px-4 py-3 bg-gray-900 border border-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-700"
                                />
                                <button
                                    onClick={searchVaults}
                                    disabled={searchLoading}
                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 px-4 py-1 bg-white text-black rounded hover:bg-gray-100 disabled:opacity-50"
                                >
                                    {searchLoading ? '...' : 'Search'}
                                </button>
                            </div>

                            {searchResults.length > 0 && (
                                <div className="space-y-2">
                                    <h3 className="text-base font-medium text-white">Search Results</h3>
                                    {searchResults.map((vault) => (
                                        <div key={vault.id} className="p-3 bg-gray-900 border border-gray-800 rounded-lg flex items-center justify-between">
                                            <div>
                                                <h4 className="text-white font-medium">{vault.name}</h4>
                                                <p className="text-gray-400 text-sm">/{vault.slug}</p>
                                            </div>
                                            <button
                                                onClick={() => joinVault(vault.slug)}
                                                className="px-3 py-1.5 bg-white text-black rounded hover:bg-gray-100"
                                            >
                                                Join
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {searchSlug && searchResults.length === 0 && !searchLoading && (
                                <div className="text-center py-8">
                                    <p className="text-gray-400 mb-4">No vaults found matching "{searchSlug}"</p>
                                    <button
                                        onClick={() => setStep('create-vault')}
                                        className="px-5 py-2.5 bg-white text-black rounded hover:bg-gray-100"
                                    >
                                        Create Your Own Vault
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="text-center">
                            <button
                                onClick={() => setStep('choose-action')}
                                className="text-gray-300 hover:text-white underline"
                            >
                                Back to options
                            </button>
                            <div className="mt-2">
                                <button
                                    type="button"
                                    onClick={onComplete}
                                    className="text-gray-300 hover:text-white underline text-sm"
                                >
                                    Back to sign in
                                </button>
                            </div>
                        </div>
                    </div>
                );

            case 'create-vault':
                return (
                    <div className="space-y-6">
                        <div className="text-center">
                            <h2 className="text-3xl font-light text-white mb-3">Create Your Vault</h2>
                            <p className="text-gray-400">Set up your vault space. Unlimited vaults during development.</p>
                        </div>

                        <div className="space-y-6">
                            {/* Logo Upload */}
                            <div className="text-center">
                                <div
                                    className="relative w-24 h-24 bg-gray-800 border-2 border-dashed border-gray-600 rounded-md flex items-center justify-center overflow-hidden mx-auto mb-4 cursor-pointer hover:border-gray-500 transition-colors"
                                    onClick={() => document.getElementById('vault-logo-input')?.click()}
                                >
                                    {vaultLogoPreview ? (
                                        <img
                                            src={vaultLogoPreview}
                                            alt="Vault logo preview"
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <Upload className="w-6 h-6 text-gray-400" />
                                    )}
                                </div>
                                <input
                                    id="vault-logo-input"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleLogoUpload}
                                    className="hidden"
                                />
                                <p className="text-xs text-gray-500">Upload vault logo (optional)</p>
                            </div>

                            {/* Vault Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Vault Name</label>
                                <input
                                    type="text"
                                    value={vaultName}
                                    onChange={(e) => handleVaultNameChange(e.target.value)}
                                    placeholder="My Family Vault"
                                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* Plan Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Plan</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {(['personal', 'family', 'business'] as VaultPlan[]).map(p => {
                                        const active = p === planChoice;
                                        return (
                                            <button
                                                type="button"
                                                key={p}
                                                onClick={() => setPlanChoice(p)}
                                                className={`px-3 py-2 rounded-md text-sm font-medium border transition-colors ${active ? 'bg-white text-black border-white shadow' : 'bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700'}`}
                                                aria-pressed={active}
                                            >
                                                {p.charAt(0).toUpperCase() + p.slice(1)}
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="mt-2 text-xs text-gray-400">You can change this later.</p>
                            </div>

                            {/* Vault Link (Slug) */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Vault Link</label>
                                <input
                                    type="text"
                                    value={vaultSlug}
                                    onChange={(e) => handleSlugChange(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (debounceRef.current) {
                                                window.clearTimeout(debounceRef.current);
                                            }
                                            checkSlugAvailability(vaultSlug);
                                        }
                                    }}
                                    onBlur={() => {
                                        if (debounceRef.current) {
                                            window.clearTimeout(debounceRef.current);
                                        }
                                        if (vaultSlug) {
                                            checkSlugAvailability(vaultSlug);
                                        }
                                    }}
                                    placeholder="your-vault-link"
                                    className={
                                        `w-full px-4 py-3 bg-gray-800 text-white rounded-lg focus:outline-none transition-colors ` +
                                        `focus:ring-2 ${slugFlash === 'green' ? 'ring-green-500' : slugFlash === 'red' ? 'ring-red-500' : 'focus:ring-blue-500'}`
                                    }
                                    style={{
                                        borderWidth: 1,
                                        borderColor: slugAvailable == null || checkingSlug ? '#4b5563' : (slugAvailable ? '#22c55e' : '#ef4444'),
                                        backgroundColor: slugAvailable == null || checkingSlug ? 'rgba(31,41,55,1)' : (slugAvailable ? 'rgba(20,83,45,0.35)' : 'rgba(127,29,29,0.35)')
                                    }}
                                />
                                {/* Full URL preview below for easy copy */}
                                <div className="mt-2 flex items-center justify-between gap-2 p-2 bg-gray-900/70 border border-gray-800/70 rounded-md">
                                    <div className="text-gray-300 text-sm truncate" title={publicVaultUrl(vaultSlug)}>
                                        {displayVaultUrl(vaultSlug)}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            try {
                                                await navigator.clipboard.writeText(publicVaultUrl(vaultSlug));
                                                setCopied(true);
                                                window.setTimeout(() => setCopied(false), 1500);
                                            } catch { /* ignore */ }
                                        }}
                                        disabled={!vaultSlug}
                                        className="shrink-0 p-1.5 rounded bg-gray-800/80 border border-gray-700 text-gray-200 hover:bg-gray-700 disabled:opacity-50"
                                        aria-label="Copy full vault URL"
                                        title="Copy URL"
                                    >
                                        {copied ? <Check className="w-4 h-4" /> : <CopyIcon className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                            <button
                                onClick={createVault}
                                disabled={loading || !vaultName || !vaultSlug || !slugAvailable}
                                className="w-full py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Creating Vault...' : 'Create Vault'}
                            </button>
                        </div>

                        <div className="text-center">
                            {isAuthed ? (
                                <button
                                    onClick={() => setStep('find-vault')}
                                    className="text-blue-400 hover:text-blue-300 underline"
                                >
                                    Find/Search Vault
                                </button>
                            ) : (
                                <div className="space-y-2">
                                    <button
                                        onClick={() => setStep('choose-action')}
                                        className="text-blue-400 hover:text-blue-300 underline"
                                    >
                                        Back to options
                                    </button>
                                    <div>
                                        <button
                                            type="button"
                                            onClick={onComplete}
                                            className="text-blue-400 hover:text-blue-300 underline text-sm"
                                        >
                                            Back to sign in
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 flex items-center justify-center p-4 relative">
            {/* Page-level Back button (not part of the card) */}
            {(step === 'create-vault' || step === 'find-vault') && (
                <button
                    onClick={() => {
                        try { onComplete(); } catch { }
                    }}
                    className="fixed left-4 top-4 z-20 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gray-900/70 border border-gray-800/70 text-gray-300 hover:text-white hover:bg-gray-800/70 shadow-sm backdrop-blur-md focus:outline-none focus:ring-2 focus:ring-gray-700"
                    aria-label="Back"
                    title="Back"
                >
                    <ChevronLeft className="w-4 h-4" />
                    <span className="text-sm">Back</span>
                </button>
            )}
            <div className="max-w-md w-full space-y-8">
                <div className="text-center">
                    <img
                        src="https://res.cloudinary.com/digjsdron/image/upload/v1753107750/GriswoldVault_bjzwjw.webp"
                        alt="Vault Contact Logo"
                        className="h-16 w-16 mx-auto mb-6 object-contain"
                    />
                </div>
                <div className="relative bg-black/40 backdrop-blur-xl rounded-lg border border-gray-800/50 p-8 shadow-2xl">
                    {error && (
                        <div className="mb-6 p-4 bg-red-900/30 border border-red-800/50">
                            <p className="text-red-300 text-sm">{error}</p>
                        </div>
                    )}

                    {success && (
                        <div className="mb-6 p-4 bg-green-900/30 border border-green-800/50">
                            <p className="text-green-300 text-sm">{success}</p>
                        </div>
                    )}

                    {renderStep()}
                </div>
            </div>
        </div>
    );
}
